<template>
    <ValidationErrors :show="showErrors"/>
    <Head :title="'Quotes - ' + project.title"/>
    <div class="w-full mb-6">
        <ProjectHeader :project="project"></ProjectHeader>
        <div class="space-y-6 lg:col-span-9">
            <section aria-labelledby="payment-details-heading">
                <form @submit.prevent="submit">
                    <div class="shadow sm:rounded-md sm:overflow-hidden">
                        <div class="bg-white px-4 p-6 space-y-6">
                            <div class="flex pb-5 border-b border-gray-200 justify-between items-center">
                                <h3 class="text-lg leading-6 font-bold text-gray-900">Offers</h3>
                                <div class="flex items-center">
                                    <strong class="text-sm">Filter by:</strong>
                                    <PopoverGroup class="hidden sm:flex sm:items-baseline sm:space-x-8">
                                        <Popover as="div" id="desktop-menu"
                                                 class="ml-3 relative z-0 inline-block text-left">
                                            <div>
                                                <Link
                                                    :href="route('frontend.project.quote.index', [project.id])"
                                                    class="group inline-flex items-center justify-center text-sm font-medium text-gray-700 hover:text-gray-900">
                                                    <span class="underline">All Offers</span>
                                                    <span
                                                        class="ml-1.5 rounded py-0.5 px-1.5 bg-blue-200 text-xs font-semibold text-gray-700 tabular-nums">{{
                                                            quote_count.all
                                                        }}</span>
                                                </Link>
                                            </div>
                                        </Popover>
                                        <Popover as="div" id="desktop-menu"
                                                 class="relative z-0 inline-block text-left">
                                            <div>
                                                <Link
                                                    :href="route('frontend.project.quote.status', [project.id, 'request'])"
                                                    class="group inline-flex items-center justify-center text-sm font-medium text-gray-700 hover:text-gray-900">
                                                    <span class="underline">Request Offers</span>
                                                    <span
                                                        class="ml-1.5 rounded py-0.5 px-1.5 bg-gray-200 text-xs font-semibold text-gray-700 tabular-nums">{{
                                                            quote_count.request
                                                        }}</span>
                                                </Link>
                                            </div>
                                        </Popover>
                                        <Popover as="div" id="desktop-menu"
                                                 class="relative z-0 inline-block text-left">
                                            <div>
                                                <Link
                                                    :href="route('frontend.project.quote.status', [project.id, 'confirmed'])"
                                                    class="group inline-flex items-center justify-center text-sm font-medium text-gray-700 hover:text-gray-900">
                                                    <span class="underline">Confirmed Offers</span>
                                                    <span
                                                        class="ml-1.5 rounded py-0.5 px-1.5 bg-green-200 text-xs font-semibold text-gray-700 tabular-nums">{{
                                                            quote_count.confirmed
                                                        }}</span>
                                                </Link>
                                            </div>
                                        </Popover>
                                    </PopoverGroup>
                                </div>
                                <div class="flex text-right items-end">
                                    <Button @click="create"
                                            class="shadow-md inline-flex items-center px-3 py-2 border border-transparent shadow-sm text-sm leading-4 rounded-md text-white bg-amaze-coral focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                                        <svg class="-ml-0.5 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg"
                                             fill="none"
                                             viewBox="0 0 24 24"
                                             stroke="currentColor" aria-hidden="true">
                                            <path stroke-linecap="round" stroke-linejoin="round"
                                                  stroke-width="2"
                                                  d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                                        </svg>
                                        Create New Offer
                                    </Button>

                                </div>
                            </div>

                            <div v-if="cmid_smid_errors && cmid_smid_errors.length" class="rounded-md bg-yellow-50 p-4">
                                <div class="flex">
                                    <div class="flex-shrink-0">
                                        <InformationCircleIcon class="h-5 w-5 text-yellow-400" aria-hidden="true"/>
                                    </div>
                                    <div class="ml-3">
                                        <h3 class="text-sm font-medium text-yellow-800">CMID / SMID issues must be resolved before confirming offers</h3>
                                        <div class="mt-2 text-sm text-yellow-700">
                                            <button type="button" class="underline cursor-pointer" @click="show_cmid_smid_errors = !show_cmid_smid_errors">Click here to view issues</button>
                                            <ul v-if="show_cmid_smid_errors" class="list-disc ml-5 mt-2">
                                                <li v-for="(cmid_smid_error, index) in cmid_smid_errors" :key="index">
                                                    <Link :href="route('frontend.organisations.' + cmid_smid_error.type + '.organisation.show', [cmid_smid_error.id])"
                                                       class="underline">
                                                        {{ cmid_smid_error.name }}
                                                    </Link>
                                                </li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div class="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                                <div class="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
                                    <div class="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
                                        <table class="min-w-full divide-y divide-gray-200">
                                            <thead class="bg-gray-50">
                                            <tr>
                                                <th scope="col"
                                                    class="px-3 py-4 text-left text-xs text-gray-500 uppercase tracking-wider border-r">
                                                </th>
                                                <th scope="col"
                                                    class="px-3 py-4 text-left text-xs text-gray-500 uppercase tracking-wider border-r">
                                                    Offer
                                                </th>
                                                <th scope="col"
                                                    class="px-3 py-4 text-left text-xs text-gray-500 uppercase tracking-wider border-r">
                                                    Client
                                                </th>
                                                <th scope="col"
                                                    class="px-3 py-4 text-left text-xs text-gray-500 uppercase tracking-wider border-r">
                                                    Status
                                                </th>
                                                <th scope="col"
                                                    class="px-3 py-4 text-left text-xs text-gray-500 uppercase tracking-wider border-r">
                                                    Overall Sell
                                                </th>
                                                <th scope="col"
                                                    class="px-3 py-4 text-left text-xs text-gray-500 uppercase tracking-wider border-r">
                                                    Overall Buy
                                                </th>
                                                <th scope="col"
                                                    class="px-3 py-4 text-left text-xs text-gray-500 uppercase tracking-wider border-r">
                                                    Commission
                                                </th>
                                                <th scope="col"
                                                    class="px-3 py-4 text-left text-xs text-gray-500 uppercase tracking-wider border-r">
                                                    Profit
                                                </th>
                                                <th scope="col"
                                                    class="px-3 py-4 text-left text-xs text-gray-500 uppercase tracking-wider">
                                                    Profit %
                                                </th>
                                                <th scope="col"
                                                    class="px-3 py-4 text-left text-xs text-gray-500 uppercase tracking-wider">
                                                </th>
                                            </tr>
                                            </thead>
                                            <tbody>
                                            <tr v-if="quotes.length > 0" v-for="(quote, index) in quotes" :key="index"
                                                :class="{'bg-gray-100': quote.status === 'Request', 'bg-green-100' : quote.status === 'Confirmed'}"
                                                class="hover:bg-gray-50 cursor-pointer border">
                                                <td class="px-3 py-4 whitespace-nowrap text-sm text-gray-900 text-left border-r">
                                                    <!--<button v-if="quote.status !== 'Confirmed'" type="button"
                                                            @click="confirmQuote(quote, quote.id)" title="Confirm Quote"
                                                            class="shadow-md inline-flex mr-3 items-center px-[0.375rem] py-1 border border-transparent shadow-sm text-xs leading-4 font-medium rounded-md text-white bg-green-500 hover:bg-green-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500">
                                                        <CheckCircleIcon class="h-4 w-4"
                                                                         aria-hidden="true"/>
                                                    </button>-->
                                                    <button @click="edit(quote)"
                                                         title="Edit Quote"
                                                        class="shadow-md inline-flex items-center px-[0.375rem] py-1 border border-transparent shadow-sm text-xs leading-4 font-medium rounded-md text-white bg-gray-600 hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500">
                                                        <PencilIcon class="h-4 w-4" aria-hidden="true"/>
                                                    </button>
                                                </td>
                                                <td class="px-3 py-4 whitespace-nowrap text-sm text-gray-900 border-r ">
                                                    <Link v-if="quote.status === 'Confirmed'" :href="route('frontend.project.quote.show.accepted', [project.id, quote.id])" class="underline">{{ quote.name }}</Link>
                                                    <Link v-else :href="route('frontend.project.quote.show', [project.id, quote.id])" class="underline">{{ quote.name }}</Link>
                                                </td>
                                                <td class="px-3 py-4 whitespace-nowrap text-sm text-gray-900 border-r">
                                                    <Link
                                                        :href="route('frontend.organisations.clients.organisation.show', quote.client.id)"
                                                        class="underline">{{ quote.client.name }}
                                                    </Link>
                                                </td>
                                                <td class="px-3 py-4 whitespace-nowrap text-sm text-gray-900 border-r">
                                                    <span v-if="quote.status === 'Request'"
                                                          class="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium shadow-md bg-gray-500 text-white"> {{
                                                            quote.status
                                                        }} </span>
                                                    <span v-if="quote.status === 'Confirmed'"
                                                          class="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium shadow-md bg-gray-100 text-green-800"> {{
                                                            quote.status
                                                        }} </span>
                                                    <span v-if="quote.status === 'Cancelled'"
                                                          class="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium shadow-md text-red-200 bg-red-600"> {{
                                                            quote.status
                                                        }} </span>
                                                </td>
                                                <td class="px-3 py-4 whitespace-nowrap text-sm text-gray-900 border-r">
                                                    {{  formatCurrency(quote.subtotals.overall_sell_total, project.bank_account.currency.code, true) }}
                                                </td>
                                                <td class="px-3 py-4 whitespace-nowrap text-sm text-gray-900 border-r">
                                                    {{
                                                        formatCurrency(quote.subtotals.overall_buy_total, quote.buy_currency.code, true)
                                                    }}
                                                </td>
                                                <td class="px-3 py-4 whitespace-nowrap text-sm text-gray-900 border-r">
                                                    {{
                                                        formatCurrency(quote.subtotals.commission, project.bank_account.currency.code, true)
                                                    }}
                                                </td>
                                                <td class="px-3 py-4 whitespace-nowrap text-sm text-gray-900 border-r">
                                                    {{
                                                        formatCurrency(quote.subtotals.overall_profit, project.bank_account.currency.code, true)
                                                    }}
                                                </td>
                                                <td class="px-3 py-4 whitespace-nowrap text-sm text-gray-900 border-r">
                                                    {{
                                                        number_format(parseFloat(quote.subtotals.overall_profit_percentage).toFixed(2))
                                                    }}%
                                                </td>
                                                <td class="px-3 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                                                    <button type="button" @click="duplicateQuote(quote.id)" title="Duplicate Quote"
                                                            class="shadow-md inline-flex mr-3 items-center px-[0.375rem] py-1 border border-transparent shadow-sm text-xs leading-4 font-medium rounded-md text-black bg-gray-200 hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-200">
                                                        <ClipboardCopyIcon class="h-4 w-4"
                                                                           aria-hidden="true"/>
                                                    </button>
                                                </td>
                                            </tr>
                                            <tr v-else>
                                                <td colspan="10"
                                                    class="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                                                    There are no quotes available.
                                                </td>
                                            </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </form>
            </section>
        </div>
    </div>
    <CreateQuote ref="slideout"/>
    <DuplicateQuote ref="duplicate_quote"/>
    <ConfirmQuote :quote="quote" :project="project"/>
</template>


<script>
import Authenticated from "@/Layouts/Authenticated.vue";
import {Link, Head} from "@inertiajs/vue3";
import {
    ClipboardCopyIcon,
    PlusSmIcon,
    UsersIcon,
    InformationCircleIcon,
    ChevronDownIcon,
    CogIcon,
    PencilIcon,
    CheckCircleIcon
} from "@heroicons/vue/outline"
import ValidationErrors from "@/Components/ValidationErrors";
import ProjectHeader from "@/Components/ProjectHeader";
import CreateQuote from "@/Components/SlideOuts/CreateQuote";
import {Popover, PopoverButton, PopoverGroup} from "@headlessui/vue";
import Button from "@/Components/Button.vue";
import { router } from '@inertiajs/vue3'
import ConfirmQuote from "@/Components/SlideOuts/ConfirmQuote.vue";
import {emitter} from "@/eventHub";

import DuplicateQuote from "@/Components/SlideOuts/DuplicateQuote";

export default {
    layout: Authenticated,
    components: {
        DuplicateQuote,
        Button,
        CreateQuote,
        ProjectHeader,
        ValidationErrors,
        Link,
        Head,
        PlusSmIcon,
        UsersIcon,
        InformationCircleIcon,
        Popover,
        PopoverButton,
        ChevronDownIcon,
        PopoverGroup,
        CogIcon,
        PencilIcon,
        ClipboardCopyIcon,
        CheckCircleIcon,
        ConfirmQuote
    },
    props: {
        project: Object,
        quotes: Object,
        quote_count: Object,
        cmid_smid_errors: {
            type: Array,
            default: () => []
        }
    },
    data() {
        return {
            showErrors: false,
            show_cmid_smid_errors: false,
            emitter: emitter,
        }
    },
    methods: {
        create() {
            this.$refs.slideout.updating = false;
            this.$refs.slideout.client_id = this.project.client.id;
            this.$refs.slideout.open();
        },
        edit(quote) {
            this.$refs.slideout.updating = true;
            this.$refs.slideout.form.id = quote.id;
            this.$refs.slideout.form.name = quote.name;
            this.$refs.slideout.form.client = quote.client.name;
            this.$refs.slideout.form.client_id = quote.client_id;
            this.$refs.slideout.form.status = quote.status;
            this.$refs.slideout.form.buy_currency_id = quote.buy_currency_id;
            this.$refs.slideout.form.exchange_rate = quote.exchange_rate;
            this.$refs.slideout.form.project_id = quote.project_id;
            this.$refs.slideout.open();
        },
        /*confirmQuote(id) {
            this.$swal.fire({
                title: 'Confirm Quote',
                text: "You are confirming a quote. Are you sure you'd like to do this?",
                showCloseButton: true,
                showCancelButton: true,
                confirmButtonText: 'Yes',
                cancelButtonText: 'No',
                stopKeydownPropagation: true,
                keydownListenerCapture: true
            })
                .then((result) => {
                    if (result.isConfirmed) {
                        this.$inertia.post(route('frontend.project.quote.confirm', [this.project.id, id]), {}, {
                            preserveScroll: true,
                            onError: (page) => {
                                this.loading = false
                                this.showErrors = true
                            }
                        });
                    }
                });
        },*/
        confirmQuote(quote, id) {
            this.emitter.emit("openConfirmQuoteSlideout", {quote: quote, id: id})
        },
        duplicateQuote(id) {
            this.$refs.duplicate_quote.form.quote_id = id;
            this.$refs.duplicate_quote.open();
        },
        number_format(nStr) {
            nStr += '';
            let x = nStr.split('.');
            let x1 = x[0];
            let x2 = x.length > 1 ? '.' + x[1] : '';
            let rgx = /(\d+)(\d{3})/;
            while (rgx.test(x1)) {
                x1 = x1.replace(rgx, '$1' + ',' + '$2');
            }
            return x1 + x2;
        },
        doRounding(value, decimals) {
            return Number(Math.round(value + "e" + decimals) + "e-" + decimals).toFixed(2);
        },
    }
}
</script>
